import React from "react";
import JSZip from "jszip";
import { Autocomplete, Box, Checkbox, Chip, TextField } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import globals from "../../Globals";
import { countryName } from "../../Layout/CountryPicker";

// What the LiveOps sub-pages built on the liveops slot store share (Offers, Events): the roles, the UTC dates, the
// slots (status, order, versions, platforms), the Unity bundle zip check. Pure helpers and small components only;
// each page keeps its own state and dialogs.
export const ALL = "*";                 // the selects' "no filter" value (MUI shows "" as no selection)
export const ID_RE = /^[A-Za-z0-9_-]{1,40}$/;
export const MAX_PATCH = 0x1FFF;
// The owner's rule: fixed layout and an explicit width on every column
export const FIXED = { "& table": { tableLayout: "fixed" }, "& .MuiTableCell-root": { px: 1, py: 0.75, fontSize: 13 } };
export const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
export const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };
export const Scroll = ({ children, height = 340 }) => <Box sx={{ maxHeight: height, overflowY: "auto", mt: 1 }}>{children}</Box>;

// ---------- roles ----------
const titleRoles = () => {
    const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
    return Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
};
const isMaster = () => !!globals.loginData?.titles?.isMaster;
export const canEdit = () => isMaster() || titleRoles().includes("liveops");
// the versioning reads (client versions, the store catalogue) are open to `database` and the players view role
export const canReadVersioning = () => isMaster() || titleRoles().some(r => ["database", "#database", "players", "#players"].includes(r));

// ---------- dates (UTC) ----------
// The backend answers UTC; a date without a zone designator is UTC too
export const asDate = (iso) => {
    if (!iso) return null;
    const s = String(iso);
    const d = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(s) ? s : s + "Z");
    return isNaN(d.getTime()) ? null : d;
};
export const utc = (iso) => asDate(iso)?.toISOString().slice(5, 16).replace("T", " ") ?? "—";
export const utcFull = (iso) => { const d = asDate(iso); return d ? d.toISOString().slice(0, 16).replace("T", " ") + " UTC" : "—"; };
// A datetime-local input holds UTC here: its value is read as UTC and shown back from UTC
export const toUtcInput = (iso) => asDate(iso)?.toISOString().slice(0, 16) ?? "";
export const fromUtcInput = (v) => { if (!v) return ""; const d = new Date(v + "Z"); return isNaN(d.getTime()) ? "" : d.toISOString(); };
// A window's length: "45 min", "6h", "3d", "2d 12h"
export const span = (ms) => {
    const min = Math.round(ms / 60000);
    if (min < 60) return `${min} min`;
    const h = Math.round(ms / 3600000), d = Math.floor(h / 24);
    return d ? `${d}d${h % 24 ? ` ${h % 24}h` : ""}` : `${h}h`;
};

// ---------- slots ----------
const SLOT_STATUS = {
    live: { label: "live", color: "success", variant: "filled" },
    upcoming: { label: "upcoming", color: "info", variant: "filled" },
    ended: { label: "ended", color: "default", variant: "outlined" },
    off: { label: "off", color: "default", variant: "outlined" },
};
export const slotStatus = (s, now = Date.now()) => {
    if (!s.enabled) return "off";
    if (now < (asDate(s.start)?.getTime() ?? 0)) return "upcoming";
    if (now > (asDate(s.end)?.getTime() ?? 0)) return "ended";
    return "live";
};
export const SlotChip = ({ status }) => {
    const st = SLOT_STATUS[status];
    return <Chip size="small" color={st.color} variant={st.variant} label={st.label} sx={{ height: 20, minWidth: 72 }} />;
};
// live first (ending first), then upcoming (starting first), then the past (latest first); a disabled slot sorts by its time
const slotRank = (s, now) => { const t = slotStatus({ ...s, enabled: true }, now); return t === "live" ? 0 : t === "upcoming" ? 1 : 2; };
export const sortSlots = (slots, now) => [...slots].sort((a, b) => {
    const ra = slotRank(a, now), rb = slotRank(b, now);
    if (ra !== rb) return ra - rb;
    const ta = ra === 1 ? asDate(a.start) : asDate(a.end), tb = rb === 1 ? asDate(b.start) : asDate(b.end);
    return (ra === 2 ? -1 : 1) * ((ta?.getTime() ?? 0) - (tb?.getTime() ?? 0));
});
// A new slot: from the next full UTC hour, for 3 days
export const emptySlot = (key) => {
    const start = new Date();
    start.setUTCMinutes(0, 0, 0);
    start.setUTCHours(start.getUTCHours() + 1);
    const end = new Date(start.getTime() + 3 * 86400000);
    return { key: key ?? "", start: start.toISOString(), end: end.toISOString(), platforms: [], minVersion: 0, maxVersion: 0, countries: [], enabled: true, note: "" };
};

// ---------- client versions (compact ints carry the branch) ----------
export const verLabel = (compact) => {
    if (!compact) return "";
    const { major, minor, patch } = globals.decompactVersion(compact);
    return `${major}.${minor}.${patch === MAX_PATCH ? "*" : patch}`;
};
export const verRange = (min, max) => (!min && !max ? "any" : `${min ? verLabel(min) : "…"} – ${max ? verLabel(max) : "…"}`);

// The major.minor rows of a branch in the versioning list, each with its client patches, newest first
export function minorVersionsOf(list, branch) {
    const bi = globals.branches.indexOf(branch);
    const row = (list ?? []).find(b => b.branch === bi);
    const out = [];
    for (const v of row?.versions ?? []) {
        const { major, minor } = globals.decompactVersion(v.version);
        let e = out.find(x => x.major === major && x.minor === minor);
        if (!e) { e = { major, minor, patches: [] }; out.push(e); }
        for (const c of v.client ?? []) if (!e.patches.includes(c.patch)) e.patches.push(c.patch);
    }
    out.forEach(e => e.patches.sort((a, b) => b - a));
    return out.sort((a, b) => (b.major - a.major) || (b.minor - a.minor));
}
export const clientPatchesOf = (list, branch) => {
    const bi = globals.branches.indexOf(branch);
    return minorVersionsOf(list, branch).flatMap(v => v.patches.map(p => ({ value: globals.compactVersion(v.major, v.minor, p, bi), label: `${v.major}.${v.minor}.${p}` })));
};
// "1.2.3" (on the branch) or a compact int -> compact; null when unreadable
export const parseClient = (text, branch) => {
    const t = (text ?? "").trim();
    const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(t);
    if (m) return globals.compactVersion(+m[1], +m[2], +m[3], globals.branches.indexOf(branch));
    return /^\d+$/.test(t) ? parseInt(t, 10) : null;
};
// A slot's version gate is on the client's compact version: a minimum "M.m.0" / a maximum "M.m.*" covers a whole
// major.minor; the client patches of the versioning list pin one build
export function slotVersionOptions(list, branch, kind, current) {
    const bi = globals.branches.indexOf(branch);
    const opts = [{ value: 0, label: kind === "min" ? "Any (no minimum)" : "Any (no maximum)" }];
    const seen = new Set([0]);
    const add = (value, label) => { if (!seen.has(value)) { seen.add(value); opts.push({ value, label }); } };
    for (const v of minorVersionsOf(list, branch)) {
        add(globals.compactVersion(v.major, v.minor, kind === "min" ? 0 : MAX_PATCH, bi), `${v.major}.${v.minor}.${kind === "min" ? "0" : "*"} · all of ${v.major}.${v.minor}`);
        for (const p of v.patches) add(globals.compactVersion(v.major, v.minor, p, bi), `${v.major}.${v.minor}.${p}`);
    }
    if (current && !seen.has(current)) opts.push({ value: current, label: verLabel(current) });
    return opts;
}

// A multi-select of platforms (empty = every platform), modelled on CountryPicker
export function PlatformPicker({ value, onChange, disabled, sx }) {
    const selected = (value ?? []).filter(p => globals.platformNames.includes(p));
    return (
        <Autocomplete
            multiple disableCloseOnSelect size="small" options={globals.platformNames} value={selected} disabled={disabled}
            onChange={(e, v) => onChange(v)}
            renderOption={(props, p, { selected: on }) => {
                const { key, ...rest } = props;
                return (
                    <li key={key} {...rest}>
                        <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={on} sx={{ mr: 1, p: 0 }} />
                        {p}
                    </li>
                );
            }}
            renderValue={(vals, getTagProps) => vals.map((p, i) => { const { key, ...tag } = getTagProps({ index: i }); return <Chip key={key} size="small" label={p} {...tag} />; })}
            renderInput={params => <TextField {...params} label="Platforms" placeholder={selected.length ? "" : "Every platform"} />}
            sx={sx}
        />
    );
}

// The countries for a preview's single pick, by name (built once, on first use)
let countryList = null;
export function countryOptions() {
    if (!countryList) countryList = [...globals.countryCodes].sort((a, b) => countryName(a).localeCompare(countryName(b)));
    return countryList;
}

// ---------- the bundle zip (Unity's LiveOpsBundleBuild) ----------
export function formatSize(bytes) {
    if (typeof bytes !== "number" || isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}
// manifest.json { owner, unity, changeset, bundles: [{ name, platform, file, sha256, size, deps }] } + the files;
// the zip must carry the key's own bundle liveops/<key> (key = the offer id / event id)
export async function checkBundleZip(file, key) {
    const zip = await JSZip.loadAsync(file);
    const files = new Map();
    zip.forEach((path, f) => { if (!f.dir) files.set(path.replace(/\\/g, "/"), f); });
    const mf = files.get("manifest.json");
    if (!mf) throw new Error("manifest.json not found in the zip.");
    let manifest;
    try { manifest = JSON.parse(await mf.async("string")); } catch { throw new Error("manifest.json is not valid JSON."); }
    const bundles = Array.isArray(manifest?.bundles) ? manifest.bundles : [];
    if (bundles.length === 0) throw new Error("manifest.json lists no bundles.");
    const expected = "liveops/" + key;
    const own = bundles.filter(b => b.name === expected);
    return {
        owner: manifest.owner ?? "", unity: manifest.unity ?? "", changeset: manifest.changeset ?? 0, bundles, expected,
        hasOwn: own.length > 0, platforms: [...new Set(own.map(b => b.platform))],
        names: [...new Set(bundles.map(b => b.name))],
        missing: bundles.filter(b => !b.file || !files.has(String(b.file).replace(/\\/g, "/"))).map(b => `${b.name} (${b.platform})`),
    };
}
export const UPLOAD_STATUS = {
    new: { label: "new", color: "success" }, bytes: { label: "new bytes", color: "warning" }, meta: { label: "metadata", color: "info" },
    same: { label: "unchanged", color: "default" }, error: { label: "error", color: "error" },
};
export const uploadStatus = (r) => (r.error ? "error" : r.changed ? (r.oldSize ? "bytes" : "new") : r.metaChanged ? "meta" : "same");

export const int = (v, def = 0) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : def; };
